import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import Api from '../services/api';

// Setup global axios interceptor - NO automatic logout except when account was deleted
let interceptorSetup = false;
if (!interceptorSetup) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        const data = error.response?.data || {};
        const isAccountDeleted = data.code === 'ACCOUNT_DELETED' || data.message === 'User not found';
        if (isAccountDeleted) {
          console.log('🗑️ Account deleted detected from API – forcing logout');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('user-deleted', {
              detail: { message: data.message || 'Your account has been deleted by the administrator.' }
            }));
          }
        } else {
          console.log('⚠️ 401 Unauthorized detected, but NOT logging out automatically');
        }
      }
      return Promise.reject(error);
    }
  );
  interceptorSetup = true;
}

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper functions to safely access localStorage (outside component for reuse)
const safeLocalStorageGet = (key) => {
    try {
      if (typeof Storage !== 'undefined' && localStorage) {
        return localStorage.getItem(key);
      }
      console.warn('⚠️ localStorage is not available');
      return null;
    } catch (error) {
      console.error('❌ Error accessing localStorage:', error);
      // Chrome might block localStorage in certain privacy modes
      if (error.name === 'SecurityError' || error.name === 'QuotaExceededError') {
        console.error('⚠️ Chrome localStorage access blocked or quota exceeded');
      }
      return null;
    }
  };

// Helper function to safely set localStorage
const safeLocalStorageSet = (key, value) => {
    try {
      if (typeof Storage !== 'undefined' && localStorage) {
        localStorage.setItem(key, value);
        return true;
      }
      console.warn('⚠️ localStorage is not available');
      return false;
    } catch (error) {
      console.error('❌ Error setting localStorage:', error);
      if (error.name === 'QuotaExceededError') {
        console.error('⚠️ localStorage quota exceeded - clearing old data');
        try {
          // Try to clear some old data
          localStorage.removeItem('current_booking_data');
          localStorage.removeItem('trip_started_booking');
          // Retry
          localStorage.setItem(key, value);
          return true;
        } catch (retryError) {
          console.error('❌ Retry failed:', retryError);
        }
      }
      return false;
    }
  };

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on app load and validate it with backend
    const checkExistingToken = async () => {
      try {
        // Check if localStorage is available
        try {
          const testKey = '__localStorage_test__';
          localStorage.setItem(testKey, 'test');
          localStorage.removeItem(testKey);
        } catch (testError) {
          console.error('❌ localStorage is not accessible:', testError);
          setLoading(false);
          return;
        }

        // Get token directly from localStorage (same way as booking data)
        const storedToken = localStorage.getItem('uttaapp_token');
        const storedUser = localStorage.getItem('uttaapp_user');
        
        if (storedToken && storedUser) {
          console.log('🔍 Found existing token in localStorage, validating with backend...');
          console.log('📦 Token length:', storedToken.length);
          console.log('📦 User data exists:', !!storedUser);
          console.log('📦 Token retrieved same way as booking data - should persist');
          
          // Set token and user immediately to prevent logout flash
          setToken(storedToken);
          try {
            setUser(JSON.parse(storedUser));
          } catch (parseError) {
            console.error('Error parsing stored user:', parseError);
          }
          
          try {
            // Validate token with backend (with timeout)
            const response = await axios.post(Api.Auth.validateToken, {
              token: storedToken
            }, {
              timeout: 10000 // 10 second timeout
            });
            
            if (response.data.success) {
              console.log('✅ Token is valid, user authenticated');
              setToken(storedToken);
              // Merge backend user with cached user to avoid losing fields when backend sends partial user payload
              let cachedUser = null;
              try {
                cachedUser = storedUser ? JSON.parse(storedUser) : null;
              } catch (parseError) {
                console.error('Error parsing cached user for merge:', parseError);
              }

              const mergedUser = {
                ...(cachedUser || {}),
                ...(response.data.user || {})
              };

              setUser(mergedUser);
              // Update localStorage with latest merged user data
              safeLocalStorageSet('uttaapp_user', JSON.stringify(mergedUser));
            } else {
              // NEVER automatically logout - keep user logged in with cached data
              // Only manual logout button should logout user
              console.log('⚠️ Token validation returned false, but keeping user logged in with cached data');
              console.log('   Note: User will remain logged in. Only manual logout will clear session.');
              setToken(storedToken);
              try {
                setUser(JSON.parse(storedUser));
              } catch (parseError) {
                console.error('Error parsing stored user:', parseError);
              }
            }
          } catch (validationError) {
            // NEVER automatically logout - keep user logged in with cached data
            // Only manual logout button should logout user
            console.log('⚠️ Token validation failed, but keeping user logged in with cached data');
            console.log('   Error:', validationError.code || validationError.message);
            console.log('   Status:', validationError.response?.status || 'N/A');
            console.log('   Note: User will remain logged in. Only manual logout will clear session.');
            
            // Keep user logged in with cached token and user data
            setToken(storedToken);
            try {
              setUser(JSON.parse(storedUser));
            } catch (parseError) {
              console.error('Error parsing stored user:', parseError);
              // Only clear if data is truly corrupted, not just validation failed
              // Don't clear on network errors
              if (validationError.code !== 'ERR_NETWORK' && validationError.code !== 'ECONNABORTED') {
                try {
                  localStorage.removeItem('uttaapp_token');
                  localStorage.removeItem('uttaapp_user');
                } catch (clearError) {
                  console.error('Error clearing corrupted data:', clearError);
                }
              }
            }
          }
        } else {
          // No stored token/user, user is not logged in
          console.log('ℹ️ No stored token found');
          if (!storedToken) {
            console.log('   Token missing from localStorage');
          }
          if (!storedUser) {
            console.log('   User data missing from localStorage');
          }
        }
      } catch (error) {
        console.error('Error checking existing token:', error);
        // Only clear if we have a stored token but something went wrong
        const storedToken = safeLocalStorageGet('uttaapp_token');
        const storedUser = safeLocalStorageGet('uttaapp_user');
        if (storedToken && storedUser) {
          // Keep user logged in on unexpected errors
          console.log('⚠️ Unexpected error, keeping user logged in with cached data');
          setToken(storedToken);
          try {
            setUser(JSON.parse(storedUser));
          } catch (parseError) {
            console.error('Error parsing stored user:', parseError);
            // Only clear if truly corrupted
            try {
              localStorage.removeItem('uttaapp_token');
              localStorage.removeItem('uttaapp_user');
            } catch (clearError) {
              console.error('Error clearing data:', clearError);
            }
          }
        }
      } finally {
        setLoading(false);
      }
    };

    checkExistingToken();
  }, []);

  const login = (userData, authToken) => {
    try {
      // Validate inputs
      if (!authToken) {
        console.error('❌ Login failed: No token provided');
        throw new Error('Authentication token is required');
      }
      
      if (!userData) {
        console.error('❌ Login failed: No user data provided');
        throw new Error('User data is required');
      }

      console.log('🔐 Logging in user:', userData.email);
      console.log('🔑 Token received:', authToken ? `${authToken.substring(0, 20)}...` : 'NULL');
      
      // Set state first to prevent logout flash
      setToken(authToken);
      setUser(userData);
      
      // Store in localStorage - save directly like booking data for reliability
      try {
        // Save token directly to localStorage (same way as booking data)
        localStorage.setItem('uttaapp_token', authToken);
        localStorage.setItem('uttaapp_user', JSON.stringify(userData));
        
        // Verify storage immediately
        const storedToken = localStorage.getItem('uttaapp_token');
        const storedUser = localStorage.getItem('uttaapp_user');
        
        if (storedToken && storedUser) {
          console.log('✅ Token and user data stored successfully in localStorage');
          console.log('📦 Stored token:', storedToken.substring(0, 20) + '...');
          console.log('📦 Token saved same way as booking data - will persist across page refreshes');
        } else {
          console.error('❌ Failed to verify token/user data in localStorage');
          console.error('   Token stored:', !!storedToken);
          console.error('   User stored:', !!storedUser);
          // Retry with safe function as fallback
          safeLocalStorageSet('uttaapp_token', authToken);
          safeLocalStorageSet('uttaapp_user', JSON.stringify(userData));
        }
      } catch (storageError) {
        console.error('❌ Error storing token/user data:', storageError);
        
        // If quota exceeded, try clearing old booking data and retry
        if (storageError.name === 'QuotaExceededError') {
          console.warn('⚠️ localStorage quota exceeded, clearing old booking data and retrying...');
          try {
            // Clear old booking data but keep token
            const existingToken = localStorage.getItem('uttaapp_token');
            // eslint-disable-next-line no-unused-vars
            const existingUser = localStorage.getItem('uttaapp_user');
            localStorage.removeItem('current_booking_data');
            localStorage.removeItem('trip_started_booking');
            // Retry saving token
            if (existingToken) localStorage.setItem('uttaapp_token', existingToken);
            localStorage.setItem('uttaapp_token', authToken);
            localStorage.setItem('uttaapp_user', JSON.stringify(userData));
            console.log('✅ Token saved after clearing old data');
          } catch (retryError) {
            console.error('❌ Retry failed:', retryError);
            alert('⚠️ Warning: Unable to save login session. localStorage is full. Please clear browser data.');
          }
        } else {
          // For other errors, try safe function
          safeLocalStorageSet('uttaapp_token', authToken);
          safeLocalStorageSet('uttaapp_user', JSON.stringify(userData));
        }
      }
      
      // Initialize socket connection after login
      if (userData && !userData.isBlocked) {
        const { getSocket } = require('../services/socket');
        setTimeout(() => {
          const socket = getSocket();
          if (socket) {
            console.log('🔌 Socket initialized after login');
          }
        }, 500);
      }
    } catch (error) {
      console.error('❌ Error in login function:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (token) {
        // Call logout API to invalidate token on server
        await axios.post(Api.Auth.logout, { token });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear state and localStorage regardless of API call success
      console.log('👋 Logging out user - clearing token from localStorage');
      setToken(null);
      setUser(null);
      // Clear token same way as booking data is cleared
      localStorage.removeItem('uttaapp_token');
      localStorage.removeItem('uttaapp_user');
      console.log('✅ Token cleared from localStorage');
    }
  };

  const updateUser = (updatedUserData) => {
    console.log('🔄 Updating user data');
    setUser(updatedUserData);
    localStorage.setItem('uttaapp_user', JSON.stringify(updatedUserData));
  };

  const value = {
    token,
    user,
    login,
    logout,
    updateUser,
    loading,
    isAuthenticated: !!token && !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
